import { useCallback, useEffect, useRef, useState } from "react";
import { useApiClient } from "../apiClientContext";
import { useAuthConfig } from "../auth/authContext";
import { PUSH_SUBSCRIPTION_CHANGED, type PushSubscriptionChangedMessage } from "./swMessages";

/**
 * Feature flag mirroring the Galileo rollout switch (see PRESENCE_ENABLED).
 * Push defaults on and is disabled only by an explicit `VITE_PUSH_ENABLED=false`,
 * so the toggle can be withdrawn via config without a code change. Even when
 * on, the toggle stays hidden unless a VAPID public key is configured and the
 * browser supports push.
 */
const PUSH_FLAG_ENABLED: boolean = ((): boolean => {
    const raw = import.meta.env["VITE_PUSH_ENABLED"] as string | undefined;
    return raw === undefined || raw.toLowerCase() !== "false";
})();

/**
 * The master toggle's externally-visible state.
 *  - `unsupported` — browser lacks service worker / Push API / Notification.
 *  - `denied`      — the user blocked notifications at the OS/browser level.
 *  - `off`         — supported and permitted-or-undecided, not subscribed.
 *  - `on`          — subscribed on this device, registered with the server.
 *  - `busy`        — a subscribe/unsubscribe round-trip is in flight.
 */
export type PushToggleStatus = "unsupported" | "denied" | "off" | "on" | "busy";

export interface UsePushRegistrationResult {
    /**
     * Whether the feature is provisioned at all (flag on + VAPID key present).
     * When false the caller should not render the toggle — there is nothing to
     * toggle.
     */
    readonly available: boolean;
    readonly status: PushToggleStatus;
    /** Enable when off, disable when on; ignored while busy/denied/unsupported. */
    toggle: () => void;
}

function browserSupportsPush(): boolean {
    return (
        typeof navigator !== "undefined" &&
        "serviceWorker" in navigator &&
        typeof window !== "undefined" &&
        "PushManager" in window &&
        "Notification" in window
    );
}

/**
 * VAPID public keys are distributed base64url-encoded; the Push API's
 * `applicationServerKey` wants the raw bytes. Standard conversion.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    const output = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        output[i] = rawData.charCodeAt(i);
    }
    return output;
}

/**
 * Owns the per-device Web Push subscription lifecycle: permission, browser
 * subscription, and the server-side registration. The master notifications
 * toggle binds to this — it is intentionally device-scoped (turning it off
 * unsubscribes *this* browser, not the user's other devices).
 */
export function usePushRegistration(): UsePushRegistrationResult {
    const apiClient = useApiClient();
    const config = useAuthConfig();
    const vapidPublicKey = config.push?.vapidPublicKey;

    const available =
        PUSH_FLAG_ENABLED && typeof vapidPublicKey === "string" && vapidPublicKey.length > 0;

    const [status, setStatus] = useState<PushToggleStatus>("off");

    // Guard against setState after unmount during the async round-trips.
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const safeSetStatus = useCallback((next: PushToggleStatus) => {
        if (isMounted.current) {
            setStatus(next);
        }
    }, []);

    // Single site that registers a browser subscription with the backend (the
    // POST contract lives in one place). Idempotent: the server upserts by
    // endpoint, so re-registering the same device is a no-op. Used by the
    // explicit enable, the mount reconcile, and the rotation handler.
    const registerWithServer = useCallback(
        async (subscription: PushSubscription): Promise<boolean> => {
            const json = subscription.toJSON();
            const p256dh = json.keys?.["p256dh"];
            const auth = json.keys?.["auth"];
            if (json.endpoint === undefined || p256dh === undefined || auth === undefined) {
                return false;
            }
            const response = await apiClient.fetch("/push/subscriptions", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    endpoint: json.endpoint,
                    keys: { p256dh, auth },
                    deviceLabel: navigator.userAgent,
                }),
            });
            return response.ok;
        },
        [apiClient],
    );

    // Derive the initial status from the live permission + existing
    // subscription so the toggle reflects reality on mount (e.g. a device that
    // subscribed in a previous session shows "on").
    useEffect(() => {
        if (!available) {
            return;
        }
        if (!browserSupportsPush()) {
            safeSetStatus("unsupported");
            return;
        }
        let cancelled = false;
        void (async () => {
            if (Notification.permission === "denied") {
                if (!cancelled) safeSetStatus("denied");
                return;
            }
            try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                if (subscription === null) {
                    if (!cancelled) safeSetStatus("off");
                    return;
                }
                // Reconcile the live browser endpoint with the server on every
                // load: a pushsubscriptionchange while the app was closed can
                // leave the server pointing at a stale endpoint. Idempotent
                // upsert; failure is non-fatal — the browser is subscribed, so
                // the device is still "on" and the next load retries.
                void registerWithServer(subscription);
                if (!cancelled) safeSetStatus("on");
            } catch {
                if (!cancelled) safeSetStatus("off");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [available, registerWithServer, safeSetStatus]);

    // React to the service worker's rotation signal while a page is open: the
    // worker has re-subscribed, so re-read the live subscription and register it
    // with the backend (only the page holds an auth token for the call).
    useEffect(() => {
        if (!available || !browserSupportsPush()) {
            return;
        }
        const onMessage = (event: MessageEvent) => {
            const data = event.data as PushSubscriptionChangedMessage | null;
            if (data?.type !== PUSH_SUBSCRIPTION_CHANGED) {
                return;
            }
            void (async () => {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    const subscription = await registration.pushManager.getSubscription();
                    if (subscription === null) {
                        safeSetStatus("off");
                        return;
                    }
                    safeSetStatus((await registerWithServer(subscription)) ? "on" : "off");
                } catch {
                    // Leave the displayed status unchanged on a transient error.
                }
            })();
        };
        navigator.serviceWorker.addEventListener("message", onMessage);
        return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }, [available, registerWithServer, safeSetStatus]);

    const enable = useCallback(async (): Promise<void> => {
        if (!available || vapidPublicKey === undefined || !browserSupportsPush()) {
            return;
        }
        safeSetStatus("busy");
        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                safeSetStatus(permission === "denied" ? "denied" : "off");
                return;
            }
            const registration = await navigator.serviceWorker.ready;
            const existing = await registration.pushManager.getSubscription();
            const subscription =
                existing ??
                (await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
                }));

            if (!(await registerWithServer(subscription))) {
                // Couldn't register (missing keys or server refused). Discard a
                // subscription we just created rather than leave the browser
                // subscribed to an endpoint the server doesn't know about; a
                // pre-existing one is left for the mount reconcile to retry.
                if (existing === null) {
                    await subscription.unsubscribe().catch(() => undefined);
                }
                safeSetStatus("off");
                return;
            }
            safeSetStatus("on");
        } catch {
            safeSetStatus("off");
        }
    }, [available, vapidPublicKey, registerWithServer, safeSetStatus]);

    const disable = useCallback(async (): Promise<void> => {
        if (!browserSupportsPush()) {
            return;
        }
        safeSetStatus("busy");
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription !== null) {
                // Tell the server first so a failed unsubscribe doesn't strand
                // a registration the server still believes is live; then drop
                // the browser subscription. Server delete is idempotent.
                await apiClient
                    .fetch("/push/subscriptions", {
                        method: "DELETE",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ endpoint: subscription.endpoint }),
                    })
                    .catch(() => undefined);
                await subscription.unsubscribe().catch(() => undefined);
            }
            safeSetStatus("off");
        } catch {
            safeSetStatus("off");
        }
    }, [apiClient, safeSetStatus]);

    const toggle = useCallback(() => {
        if (status === "on") {
            void disable();
        } else if (status === "off") {
            void enable();
        }
        // denied / unsupported / busy: nothing actionable.
    }, [status, enable, disable]);

    return { available, status, toggle };
}
